import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { AnimatedSection } from '../components/AnimatedSection';
const faqCategories = [{
  title: 'Free Resources',
  faqs: [{
    q: 'Is the free content really free?',
    a: 'Yes, completely. No account or credit card required. Our free video lessons and guides are available to everyone.'
  }, {
    q: 'What topics do the free resources cover?',
    a: 'Our free content covers emergency basics like choking response, CPR overview, poison awareness, and building a pet first aid kit.'
  }]
}, {
  title: 'Video Course',
  faqs: [{
    q: 'How long is the video course?',
    a: 'The complete video course includes 25+ lessons totaling approximately 6 hours of content. Watch at your own pace — you have lifetime access.'
  }, {
    q: 'Do I get a certificate with the video course?',
    a: 'Yes, you receive a Certificate of Completion after finishing all modules and passing the knowledge checks. For the full 2-year professional certification, see our Certification program.'
  }]
}, {
  title: 'Workshops & On-Site Training',
  faqs: [{
    q: 'Where does the training take place?',
    a: 'All PawVerse training is delivered on-site at your location. We travel to homes, pet businesses, offices, community spaces, and pet facilities throughout the Bay Area. We bring all equipment and materials to you.'
  }, {
    q: 'Should I have my pet present during the training?',
    a: 'No, please keep your furry friends in a separate area during the session. We use specialized canine and feline CPR mannequins for all hands-on practice to ensure a safe, distraction-free learning environment.'
  }, {
    q: 'How long is the training session?',
    a: 'Our comprehensive Pet CPR & First Aid workshop runs for approximately 4 hours, including a short break. We cover both theory and extensive hands-on practice.'
  }, {
    q: 'What should I wear?',
    a: 'Wear comfortable clothing. You will be on the floor practicing chest compressions and bandaging techniques on mannequins.'
  }, {
    q: 'What do I need to provide at my location?',
    a: 'Just a clean, open space large enough for your group to sit comfortably — a living room, conference room, or open floor area works great. We bring all training equipment, mannequins, and materials.'
  }, {
    q: 'What areas in the Bay Area do you serve?',
    a: 'We provide on-site training throughout San Jose, San Francisco, Oakland, Palo Alto, Mountain View, Sunnyvale, Santa Clara, Cupertino, and surrounding Bay Area communities.'
  }, {
    q: 'Is there a minimum group size?',
    a: 'We can accommodate private sessions for individuals and families as well as larger groups for businesses and organizations. Contact us to discuss your specific needs and get a custom quote.'
  }]
}, {
  title: 'Certification',
  faqs: [{
    q: 'Do I get a certification after the training?',
    a: 'Yes! Upon successful completion of our Certification program, you will receive a Pet CPR & First Aid Certificate valid for two years, perfect for pet professionals and businesses.'
  }, {
    q: 'Is this certification recognized by professional organizations?',
    a: 'Yes, our PetTech certification is widely recognized by pet sitting organizations, dog walking apps, and grooming associations.'
  }, {
    q: "What's the difference between the video course certificate and full certification?",
    a: "The video course certificate confirms you completed the online curriculum. The full certification includes an in-person skills assessment and is valid for 2 years — it's the professional standard recognized by pet industry organizations."
  }]
}];
function FAQAccordionItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-warm-gray-200 py-5" data-id="element-331">
      <button onClick={() => setIsOpen(!isOpen)} className="flex w-full justify-between items-center text-left group" aria-expanded={isOpen} data-id="element-332">
        <span className="font-heading text-xl text-primary-900 group-hover:text-accent-500 transition-colors pr-8" data-id="element-333">
          {question}
        </span>
        <ChevronDown className={`w-5 h-5 text-accent-500 transition-transform duration-300 shrink-0 ${isOpen ? 'rotate-180' : ''}`} data-id="element-334" />
      </button>
      <AnimatePresence data-id="element-335">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} className="overflow-hidden" data-id="element-336">
            <p className="pt-4 pb-2 text-warm-gray-700 font-body leading-relaxed" data-id="element-337">
              {answer}
            </p>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function FAQPage() {
  return <div className="w-full bg-warm-white" data-id="element-338">
      {/* Header */}
      <div className="bg-primary-900 text-warm-white py-20 lg:py-28" data-id="element-339">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-340">
          <h1 className="text-4xl lg:text-6xl font-heading mb-6" data-id="element-341">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-primary-100 font-body max-w-2xl mx-auto" data-id="element-342">
            Everything you need to know about our pet CPR and first aid
            training.
          </p>
        </div>
      </div>

      {/* FAQs */}
      <AnimatedSection className="py-20 lg:py-24" data-id="element-343">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-344">
          {faqCategories.map((category, idx) => <div key={idx} className="mb-16 last:mb-0" data-id="element-345">
              <h2 className="text-2xl font-heading text-accent-600 mb-6 border-b-2 border-accent-100 pb-2 inline-block" data-id="element-346">
                {category.title}
              </h2>
              <div className="bg-white rounded-2xl shadow-warm border border-warm-gray-100 p-6 md:p-8" data-id="element-347">
                {category.faqs.map((faq, i) => <FAQAccordionItem key={i} question={faq.q} answer={faq.a} data-id="element-348" />)}
              </div>
            </div>)}
        </div>
      </AnimatedSection>

      {/* Still have questions */}
      <AnimatedSection className="py-20 bg-primary-50" data-id="element-349">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-350">
          <MessageCircle className="w-12 h-12 text-primary-800 mx-auto mb-6" data-id="element-351" />
          <h2 className="text-3xl font-heading text-primary-900 mb-4" data-id="element-352">
            Still have questions?
          </h2>
          <p className="text-lg text-warm-gray-700 font-body mb-8" data-id="element-353">
            Can't find the answer you're looking for? Reach out to us directly
            and we'll be happy to help.
          </p>
          <Link to="/contact" className="inline-flex justify-center items-center px-8 py-4 border border-transparent text-base font-medium rounded-full text-warm-white bg-primary-800 hover:bg-primary-700 shadow-warm transition-all" data-id="element-354">
            Contact Us
          </Link>
        </div>
      </AnimatedSection>
    </div>;
}